import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAdmin, useStore } from '../store/store';
import { useData } from './dataStore';
import { useRefTarget } from './hooks';
import { RootValues, useConfirmProgressMove, useRootValues } from './context';
import { DataIcon } from './DataIcon';
import { choicesTo, isPageList, retargetChoices } from './dialogue';
import { isProgressList } from './progress';
import { Value } from './FieldView';
import {
  allFields,
  enumValues,
  fieldHelp,
  fieldLabel,
  hiddenByCondition,
  humanize,
  pickerFor,
  isAssignable,
  sectionsOf,
  settingKey,
  visibilityOf,
} from './schema';
import { same } from './values';
import type { DataClassRecord, FieldDef, OwnedField, RefValue, Values } from './types';

// Editing game-data values (milestone D2). Only fields set to "edit" under Admin → Fields are editable;
// assets and unsupported kinds never are. The server re-validates everything (pb_hooks/lib/data-validate.js).

const NEVER_EDITED = ['asset', 'unsupported'];
/** Stable empty list: a new [] per render would make the store selector loop. */
const NO_VALUES: string[] = [];

/** A fresh value for a new list entry: Unity's field-initializer defaults when the export has them. */
export function defaultValue(f: FieldDef, classes: Record<string, DataClassRecord>): unknown {
  switch (f.kind) {
    case 'int':
    case 'float':
      return 0;
    case 'bool':
      return false;
    case 'string':
      return '';
    case 'enum':
      return enumValues(classes, f.type)[0]?.name ?? null;
    case 'flags':
      return [];
    case 'list':
      return [];
    case 'vector':
      return Object.fromEntries((f.components ?? []).map((c) => [c, 0]));
    case 'color':
      return '#ffffffff';
    case 'struct': {
      const cls = f.type ? classes[f.type] : undefined;
      if (cls?.defaults) return structuredClone(cls.defaults);
      return Object.fromEntries(
        allFields(classes, f.type ?? '').map((x) => [x.name, defaultValue(x, classes)]),
      );
    }
    default:
      return null;
  }
}

interface EditableFieldsProps {
  className: string;
  values: Values;
  onChange: (next: Values) => void;
  /** Top level only: Unity's values, to mark what the design changes. */
  unityValues?: Values | null;
  /** Fields that must not change here (e.g. a key that player saves store). */
  locked?: Record<string, string>;
}

export function EditableFields(props: EditableFieldsProps) {
  const outer = useRootValues();
  // The top level provides the whole asset to nested fields (parent flags, dialogue pages).
  if (outer) return <EditableFieldsInner {...props} />;
  return (
    <RootValues.Provider value={props.values}>
      <EditableFieldsInner {...props} />
    </RootValues.Provider>
  );
}

function EditableFieldsInner({ className, values, onChange, unityValues, locked }: EditableFieldsProps) {
  const { t } = useTranslation();
  const classes = useData((s) => s.classes);
  const settings = useData((s) => s.settings);
  const root = useRootValues();
  const admin = isAdmin(useStore((s) => s.user));
  // Fields that don't apply to this entry (an objective's enemyTag unless it is a Kill) stay out of the way.
  const notApplicable = hiddenByCondition(classes, className, values, root);
  const fields = allFields(classes, className).filter((f) => !notApplicable?.has(f.name));
  const shown = fields.filter((f) => visibilityOf(f, settings) !== 'hidden');
  const hidden = fields.filter((f) => visibilityOf(f, settings) === 'hidden');
  const set = (name: string, v: unknown) => onChange({ ...values, [name]: v });

  return (
    <div className="data-fields">
      {sectionsOf(shown, settings).map((s) => (
        <section key={s.title || '_'} className="data-section">
          {s.title && <h4 className="group-label">{s.title}</h4>}
          <div className="data-grid">
            {s.fields.map((f) => (
              <EditRow
                key={f.owner + f.name}
                field={f}
                value={values[f.name]}
                unity={unityValues ? unityValues[f.name] : undefined}
                showUnity={!!unityValues}
                lockReason={locked?.[f.name]}
                onChange={(v) => set(f.name, v)}
              />
            ))}
          </div>
        </section>
      ))}
      {admin && hidden.length > 0 && (
        <details className="data-hidden">
          <summary className="muted small">{t('data.hiddenFields', { count: hidden.length })}</summary>
          <div className="data-grid">
            {hidden.map((f) => (
              <div key={f.owner + f.name} className="data-row">
                <div className="data-label">{f.label || humanize(f.name)}</div>
                <div className="data-value">
                  <Value field={f} value={values[f.name]} />
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function EditRow(props: {
  field: OwnedField;
  value: unknown;
  unity: unknown;
  showUnity: boolean;
  lockReason?: string;
  onChange: (v: unknown) => void;
}) {
  const { field: f, value, unity, showUnity, lockReason, onChange } = props;
  const { t, i18n } = useTranslation();
  const settings = useData((s) => s.settings);
  const setting = settings[settingKey(f.owner, f.name)];
  const label = fieldLabel(f, setting, i18n.language);
  const help = fieldHelp(f, setting, i18n.language);
  const editable = !lockReason && NEVER_EDITED.indexOf(f.kind) < 0 && visibilityOf(f, settings) === 'edit';
  const changed = showUnity && !same(value, unity);
  const wide =
    f.kind === 'struct' ||
    f.kind === 'list' ||
    (f.kind === 'string' && (f.multiline || (typeof value === 'string' && value.length > 60)));
  return (
    <div className={`data-row${wide ? ' wide' : ''}${changed ? ' changed' : ''}`}>
      <div className="data-label" title={help || undefined}>
        {label}
        {help && <span className="data-help">{help}</span>}
      </div>
      <div className="data-value">
        {editable ? (
          <FieldInput field={f} value={value} onChange={onChange} />
        ) : (
          <Value field={f} value={value} />
        )}
        {lockReason && <div className="muted small">🔒 {lockReason}</div>}
        {changed &&
          (f.kind === 'list' || f.kind === 'struct' ? (
            // Whole lists would drown the form: fold Unity's version away.
            <details className="unity-was small">
              <summary>
                {t('data.edit.unityHas')}{' '}
                {f.kind === 'list'
                  ? t('data.edit.entries', { count: Array.isArray(unity) ? unity.length : 0 })
                  : t('data.edit.show')}
              </summary>
              <Value field={f} value={unity} />
            </details>
          ) : (
            <div className="unity-was small">
              {t('data.edit.unityHas')} <Value field={f} value={unity} />
            </div>
          ))}
      </div>
    </div>
  );
}

export function FieldInput({
  field: f,
  value,
  onChange,
}: {
  field: FieldDef & { owner?: string };
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  const classes = useData((s) => s.classes);
  if (f.owner === 'EnyaRPG.NPC.DialoguePageChoice' && f.name === 'targetPageIndex')
    return <PageSelect value={typeof value === 'number' ? value : -1} onChange={onChange} />;
  switch (f.kind) {
    case 'int':
    case 'float':
      return (
        <NumberInput
          value={typeof value === 'number' ? value : null}
          int={f.kind === 'int'}
          min={f.range?.[0] ?? f.min}
          max={f.range?.[1]}
          onChange={onChange}
        />
      );
    case 'bool':
      return <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />;
    case 'string':
      return <TextInput field={f} value={typeof value === 'string' ? value : ''} onChange={onChange} />;
    case 'enum': {
      const opts = enumValues(classes, f.type);
      const known = opts.some((o) => o.name === value);
      return (
        <select value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
          {!known && <option value={String(value ?? '')}>{String(value ?? '—')}</option>}
          {opts.map((o) => (
            <option key={o.name} value={o.name}>
              {humanize(o.name)}
            </option>
          ))}
        </select>
      );
    }
    case 'flags':
      return <FlagsInput field={f} value={Array.isArray(value) ? value : []} onChange={onChange} />;
    case 'ref':
      return (
        <RefPicker target={f.type ?? ''} value={(value as RefValue | null) ?? null} onChange={onChange} />
      );
    case 'struct':
      return f.type ? (
        <div className="card data-struct">
          <EditableFields className={f.type} values={(value as Values) ?? {}} onChange={onChange} />
        </div>
      ) : (
        <Value field={f} value={value} />
      );
    case 'list':
      return <ListInput field={f} value={Array.isArray(value) ? value : []} onChange={onChange} />;
    case 'vector':
      return (
        <span className="row">
          {(f.components ?? []).map((c) => (
            <label key={c} className="inline small">
              {c}
              <NumberInput
                value={((value ?? {}) as Record<string, number>)[c] ?? 0}
                int={!!f.int}
                onChange={(n) => onChange({ ...((value ?? {}) as object), [c]: n })}
              />
            </label>
          ))}
        </span>
      );
    case 'color':
      return (
        <input
          type="color"
          value={typeof value === 'string' ? value.slice(0, 7) : '#ffffff'}
          onChange={(e) =>
            onChange(e.target.value + (typeof value === 'string' && value.length === 9 ? value.slice(7) : ''))
          }
        />
      );
    default:
      return <Value field={f} value={value} />;
  }
}

/** Keeps what the user types (e.g. "1.") until it is a number; always sends numbers, never locale text. */
function NumberInput(props: {
  value: number | null;
  int: boolean;
  min?: number;
  max?: number;
  onChange: (n: number) => void;
}) {
  const { value, int, min, max, onChange } = props;
  const [text, setText] = useState(value === null ? '' : String(value));
  const [shown, setShown] = useState(value);
  if (value !== shown) {
    // The value changed from outside (reset, reload): show it.
    setShown(value);
    setText(value === null ? '' : String(value));
  }
  const n = Number(text);
  const valid =
    text.trim() !== '' &&
    isFinite(n) &&
    (!int || Number.isInteger(n)) &&
    (min === undefined || n >= min) &&
    (max === undefined || n <= max);
  return (
    <input
      type="number"
      className={`num${valid ? '' : ' invalid'}`}
      step={int ? 1 : 'any'}
      min={min}
      max={max}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const v = Number(e.target.value);
        if (e.target.value.trim() !== '' && isFinite(v)) {
          setShown(v);
          onChange(v);
        }
      }}
    />
  );
}

function TextInput({
  field: f,
  value,
  onChange,
}: {
  field: FieldDef & { owner?: string };
  value: string;
  onChange: (v: string) => void;
}) {
  const listId = useId();
  const setting = useData((s) => (f.owner ? s.settings[settingKey(f.owner, f.name)] : undefined));
  const picker = pickerFor(f.name, setting);
  const vocab = useData((s) => s.lastImport?.vocabularies?.[f.name] ?? NO_VALUES);
  if (picker.startsWith('map:'))
    return <MapIdInput featureType={picker.slice(4)} value={value} onChange={onChange} />;
  if (picker === 'vocab_strict' && vocab.length) {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {!vocab.includes(value) && value && <option value={value}>{value}</option>}
        {vocab.map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </select>
    );
  }
  if (f.multiline || value.includes('\n') || value.length > 60) {
    return (
      <textarea
        rows={Math.min(12, Math.max(2, value.split('\n').length + 1))}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <>
      <input
        value={value}
        list={picker === 'vocab' ? listId : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {picker === 'vocab' && (
        <datalist id={listId}>
          {vocab.map((v) => (
            <option key={v} value={v} />
          ))}
        </datalist>
      )}
    </>
  );
}

/**
 * Class masks and other [Flags] enums. A member worth ~0 ("Everyone": all classes, including ones added
 * later) is its own checkbox and stands alone; None (0) is what an empty selection means.
 */
function FlagsInput({
  field: f,
  value,
  onChange,
}: {
  field: FieldDef;
  value: unknown[];
  onChange: (v: unknown[]) => void;
}) {
  const classes = useData((s) => s.classes);
  const defs = enumValues(classes, f.type);
  const everyone = defs.find((d) => d.value === -1);
  const none = defs.find((d) => d.value === 0);
  const singles = defs.filter((d) => d.value > 0 && (d.value & (d.value - 1)) === 0);
  const numbers = value.filter((x) => typeof x === 'number');
  const names = new Set(value.filter((x) => typeof x === 'string') as string[]);
  const all = !!everyone && names.has(everyone.name);
  const toggle = (name: string, on: boolean) => {
    const next = singles.filter((d) => (d.name === name ? on : names.has(d.name))).map((d) => d.name);
    onChange(next.length ? [...next, ...numbers] : none ? [none.name] : []);
  };
  return (
    <span className="row wrap">
      {everyone && (
        <label className="inline">
          <input
            type="checkbox"
            checked={all}
            onChange={(e) => onChange(e.target.checked ? [everyone.name] : singles.map((d) => d.name))}
          />
          {humanize(everyone.name)}
        </label>
      )}
      {singles.map((d) => (
        <label key={d.name} className={`inline${all ? ' muted' : ''}`}>
          <input
            type="checkbox"
            disabled={all}
            checked={all || names.has(d.name)}
            onChange={(e) => toggle(d.name, e.target.checked)}
          />
          {humanize(d.name)}
        </label>
      ))}
    </span>
  );
}

/** Searchable picker for a reference to another record of the right class (subclasses included). */
export function RefPicker({
  target,
  value,
  onChange,
}: {
  target: string;
  value: RefValue | null;
  onChange: (v: RefValue | null) => void;
}) {
  const { t } = useTranslation();
  const records = useData((s) => s.records);
  const classes = useData((s) => s.classes);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const box = useRef<HTMLDivElement>(null);
  const current = useRefTarget(value);
  // Assets in Unity by guid; new ones (not in Unity yet) by record id.
  const refOf = (r: { guid: string; id: string }): RefValue => (r.guid ? { guid: r.guid } : { new: r.id });
  const isCurrent = (r: { guid: string; id: string }) =>
    !!value && (r.guid ? value.guid === r.guid : value.new === r.id);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const options = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return Object.values(records)
      .filter((r) => (r.guid || r.pendingPath) && isAssignable(classes, r.className, target))
      .filter((r) => !needle || r.name.toLowerCase().includes(needle) || r.key.toLowerCase().includes(needle))
      .sort((a, b) => a.name.localeCompare(b.name, 'sv'))
      .slice(0, 80);
  }, [records, classes, target, q]);

  return (
    <div className="ref-picker" ref={box}>
      <button type="button" className="ref-chip" onClick={() => setOpen(!open)}>
        {current ? (
          <>
            <DataIcon iconKey={current.icon} size={18} />
            <span>{current.name}</span>
            {!current.guid && <span className="chip new">{t('data.create.newBadge')}</span>}
          </>
        ) : value?.guid || value?.new ? (
          <span className="warn-text">{t('data.missingRef')}</span>
        ) : (
          <span className="muted">{t('data.edit.choose')}</span>
        )}
      </button>
      {value && (
        <button
          type="button"
          className="link small"
          title={t('data.edit.clear')}
          onClick={() => onChange(null)}
        >
          ✕
        </button>
      )}
      {open && (
        <div className="picker-pop">
          <input
            autoFocus
            value={q}
            placeholder={t('data.search')}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false);
              if (e.key === 'Enter' && options[0]) {
                onChange(refOf(options[0]));
                setOpen(false);
              }
            }}
          />
          <ul>
            {options.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  className={`rec${isCurrent(r) ? ' active' : ''}`}
                  onClick={() => {
                    onChange(refOf(r));
                    setOpen(false);
                  }}
                >
                  <DataIcon iconKey={r.icon} size={18} />
                  <span className="rec-text">
                    <span className="ellipsis">{r.name}</span>
                    {r.key !== r.name && <span className="muted small ellipsis">{r.key}</span>}
                  </span>
                  {!r.guid && <span className="chip new">{t('data.create.newBadge')}</span>}
                </button>
              </li>
            ))}
            {options.length === 0 && <li className="muted pad small">{t('top.noResults')}</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

function ListInput({
  field: f,
  value,
  onChange,
}: {
  field: FieldDef & { owner?: string };
  value: unknown[];
  onChange: (v: unknown[]) => void;
}) {
  const { t } = useTranslation();
  const classes = useData((s) => s.classes);
  const confirmProgressMove = useConfirmProgressMove();
  const el = f.element;
  if (!el) return null;
  // Dialogue choices jump to pages by index: moving or deleting a page takes those jumps along.
  const pages = isPageList(f);
  // Player saves keep quest progress by step and objective position.
  const progress = isProgressList(f);
  /** `order[i]` is the old index of the entry now at i. */
  const reorder = (order: number[]) => {
    const next = order.map((j) => value[j]);
    onChange(pages ? retargetChoices(next, order, value.length) : next);
  };
  const setAt = (i: number, v: unknown) => onChange(value.map((x, j) => (j === i ? v : x)));
  const remove = (i: number) => {
    if (progress && !confirmProgressMove()) return;
    const jumps = pages ? choicesTo(value, i) : 0;
    if (jumps && !confirm(t('data.dialogue.confirmDeleteTarget', { n: i + 1, count: jumps }))) return;
    reorder(value.map((_, j) => j).filter((j) => j !== i));
  };
  const move = (i: number, d: number) => {
    if (progress && !confirmProgressMove()) return;
    const order = value.map((_, j) => j);
    [order[i], order[i + d]] = [order[i + d], order[i]];
    reorder(order);
  };
  const add = () => onChange([...value, defaultValue(el, classes)]);
  const tools = (i: number) => (
    <span className="list-tools">
      <button
        type="button"
        className="ghost icon-only small"
        disabled={i === 0}
        title={t('data.edit.up')}
        onClick={() => move(i, -1)}
      >
        ↑
      </button>
      <button
        type="button"
        className="ghost icon-only small"
        disabled={i === value.length - 1}
        title={t('data.edit.down')}
        onClick={() => move(i, 1)}
      >
        ↓
      </button>
      <button
        type="button"
        className="ghost icon-only small danger"
        title={t('common.delete')}
        onClick={() => remove(i)}
      >
        ✕
      </button>
    </span>
  );

  if (el.kind === 'struct') {
    return (
      <ol className="data-list">
        {value.map((item, i) => (
          <li key={i}>
            <span className="data-index muted small">{i + 1}</span>
            <div className="card data-struct">
              <div className="list-head">{tools(i)}</div>
              <EditableFields
                className={el.type ?? ''}
                values={(item as Values) ?? {}}
                onChange={(v) => setAt(i, v)}
              />
            </div>
          </li>
        ))}
        <li>
          <button type="button" className="small" onClick={add}>
            + {t('common.add')}
          </button>
        </li>
      </ol>
    );
  }
  if (el.kind === 'ref') {
    return (
      <div className="data-inline-list">
        {value.map((item, i) => (
          <span key={i} className="row">
            <RefPicker
              target={el.type ?? ''}
              value={(item as RefValue | null) ?? null}
              onChange={(v) => setAt(i, v)}
            />
            <button type="button" className="link small danger" onClick={() => remove(i)}>
              ✕
            </button>
          </span>
        ))}
        <RefPicker target={el.type ?? ''} value={null} onChange={(v) => v && onChange([...value, v])} />
      </div>
    );
  }
  return (
    <div className="data-fields">
      {value.map((item, i) => (
        <span key={i} className="row">
          <FieldInput field={{ ...el, name: f.name }} value={item} onChange={(v) => setAt(i, v)} />
          {tools(i)}
        </span>
      ))}
      <span>
        <button type="button" className="small" onClick={add}>
          + {t('common.add')}
        </button>
      </span>
    </div>
  );
}

/** A dialogue choice jumps to one of the dialogue's pages (by index), or closes it (-1). */
function PageSelect({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const { t } = useTranslation();
  const root = useRootValues();
  const pages = Array.isArray(root?.pages) ? (root.pages as Values[]) : [];
  const preview = (p: Values | undefined) => {
    const text = typeof p?.bodyText === 'string' ? p.bodyText.replace(/\s+/g, ' ').trim() : '';
    return text.length > 40 ? text.slice(0, 37) + '…' : text;
  };
  return (
    <select value={value} onChange={(e) => onChange(Number(e.target.value))}>
      <option value={-1}>{t('data.dialogue.close')}</option>
      {pages.map((p, i) => (
        <option key={i} value={i}>
          {t('data.dialogue.page', { n: i + 1 })}: {preview(p)}
        </option>
      ))}
      {value >= pages.length && <option value={value}>{t('data.dialogue.page', { n: value + 1 })} ?</option>}
    </select>
  );
}

/**
 * A place or NPC on the map: suggests the Unity ids of the open map's features of one type (zones'
 * zoneId, POIs' poiId, quest locations' locationTag) or NPC names. Free text stays possible, since the
 * map may not have every place yet.
 */
function MapIdInput({
  featureType,
  value,
  onChange,
}: {
  featureType: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  const listId = useId();
  const features = useStore((s) => s.features);
  const options = useMemo(() => {
    const out = new Map<string, string>();
    for (const f of Object.values(features)) {
      if (f.type !== featureType || f.status === 'deprecated') continue;
      const props = (f.properties ?? {}) as Record<string, unknown>;
      const id =
        featureType === 'npc'
          ? f.name
          : String(props.zoneId ?? props.poiId ?? props.locationTag ?? props.spawnTargetId ?? '');
      if (id) out.set(id, f.name);
    }
    return [...out.entries()].sort((a, b) => a[1].localeCompare(b[1], 'sv'));
  }, [features, featureType]);
  const known = !value || options.some(([id]) => id === value);
  return (
    <>
      <input value={value} list={listId} onChange={(e) => onChange(e.target.value)} />
      <datalist id={listId}>
        {options.map(([id, name]) => (
          <option key={id} value={id}>
            {name}
          </option>
        ))}
      </datalist>
      {!known && <div className="muted small">{t('data.edit.notOnMap')}</div>}
    </>
  );
}
